import { notifyFromResponse } from "apis/sessionExpiry";
import axios from "axios";
import { modalTypes } from "constants/types";
import errorHandler from "handlers/errorHandler";
import { showModal } from "slices/app/actions/modals";

import { getStore, setCustomHeaders } from "./utils";

export const WORKSPACE_HEADER_KEY = "X-Workspace-Id";
export const XCSRF_HEADER_KEY = "X-CSRF-TOKEN";
export const AUTHORIZATION_HEADER_KEY = "Authorization";

export const handleResponseError = (error) => {
  if (
    error?.response?.status === 403 &&
    error?.response?.data?.errorCode === "AUP_ACCEPTANCE_REQUIRED"
  ) {
    const store = getStore();
    const state = store.getState();
    const isAlreadyShowing = state.modals.some(
      (m) => m.modalType === modalTypes.AUP_ACCEPTANCE
    );
    if (!isAlreadyShowing) {
      store.dispatch(showModal(modalTypes.AUP_ACCEPTANCE));
    }
    return Promise.reject(error);
  }

  // Some non-critical calls (e.g. session config probing during setup
  // flows) may legitimately 401/403 and must not trigger the global
  // auth redirect. They opt out via the skipAuthRedirect request config.
  if (!error.config?.skipAuthRedirect) {
    errorHandler(error.response);
  }
  return Promise.reject(error);
};

const createAxios = () => {
  const { host, protocol } = location;
  const baseURL = `${protocol}//${host}`;

  const localhost = axios.create({ baseURL });

  localhost.defaults.headers.common["X-Requested-With"] = "Ajax";

  localhost.interceptors.request.use(setCustomHeaders);

  // apply interceptor on response
  localhost.interceptors.response.use(
    (response) => {
      notifyFromResponse(response);
      return response;
    },
    handleResponseError
  );

  return localhost;
};

const getAxios = () =>
  // If tests are running, then mock axios, else create new axios instance
  process.env.JEST_WORKER_ID !== undefined
    ? jest.createMockFromModule("axios").default
    : createAxios();

const localhost = getAxios();

export const downloadFileFromResponse = (
  data,
  fileName,
  { type = "" } = {}
) => {
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(link.href);
};

export const downloadWithIFrame = (url) => {
  const iframe = document.createElement("iframe");
  iframe.src = url;
  iframe.style.display = "none";
  document.body.appendChild(iframe);
};

export const METHODS = {
  get: "get",
  post: "post",
  put: "put",
  patch: "patch",
  delete: "delete",
};

export const mockRequest = (method, data, status) =>
  localhost[method].mockImplementation(() =>
    Promise.resolve({
      data,
      status,
    })
  );

export const mockRejectRequest = (method, message) => {
  const error = {
    response: {
      data: {
        error: message,
      },
    },
  };
  return localhost[method].mockImplementation(() => Promise.reject(error));
};

export default localhost;
